import React, {ReactElement, useMemo} from 'react';
import Head from 'next/head';
import Link from 'next/link';
import Error from 'next/error';
import styled from 'styled-components';

import MainLayout from 'components/layout/mainLayout';
import PageWrapper from 'components/pageWrapper';
import ContentWrapper from 'components/contentWrapper';
import Grid from 'components/blocks/blockPartials/grid';
import Card from 'components/blocks/blockPartials/card';
import Button from 'components/button';
import ProductCard from 'components/blocks/blockPartials/productCard';
import BlockHeaderWhiteBox from 'components/blocks/blockHeaderWhiteBox';
import ContentArea from 'components/contentArea';
import SbEditable from 'components/SbEditableExtended';

import seo from 'utils/seo';

import getTemplateData from 'utils/getTemplateData';
import storyblokService from 'services/storyblokService.js';


// Get sub-category link
// const getSubCatLink = (item) => {

//   if(item.content.category_type === 'page'){
//     return item.full_slug;
//   }
//   else if(item.content.category_type === 'search'){
//     return `products?${item.content.search_url}`;
//   }

// }

type Props = {
  statusCode: number,
  error?: any,
  masterTemplateData: any,
  pageData: any,
  subCats: any
};

// Category Page
const Page: NextPageExtended<Props> = (props) => {

  // Get content from supplied StoryBlok story
  const content = useMemo(() => {
    return props.pageData?.content || {};
  }, [props.pageData]);

  const seoContent = content?.seo;

  const searchUrlQuery = useMemo(() => {

    let query: ObjectOfStrings = {};

    try {
      query = JSON.parse('{"' + decodeURI(content.search_url).replace(/"/g, '\\"').replace(/&/g, '","').replace(/=/g,'":"') + '"}');
    } catch (error) {
      console.error("Badly Formatted Search URL");
    }

    if(props.pageData?.id){
      query.cat_id = props.pageData.id;
    }

    return query;

  }, [content.search_url]);


  const subCats = useMemo(() => {
      
    let subCats = props.subCats ?? [];

    subCats = subCats.map(item => {

      if(item.content.category_type === 'page'){
        item.content.link = {
          linktype: 'story',
          cached_url: item.full_slug
        }
      }

      // If we're creating a search link
      else if(item.content.search_url && item.content.search_url !== ''){

        let query: ObjectOfStrings = {};

        try {
          query = JSON.parse(`{"${decodeURI(item.content.search_url).replace(/"/g, '\\"').replace(/&/g, '","').replace(/=/g,'":"')}"}`);
        } catch (error) {
          console.error("Badly Formatted Search URL");
        }

        if(item.id){
          query.cat_id = item.id;
        }

        item.content.link = {
          linktype: 'link',
          href: {
            pathname: '/products',
            query
          }
        }

      }

      // Not a page or a search with a correct orl? Just take them to the blank product search
      else{
        item.content.link = {
          linktype: 'link',
          href: {
            pathname: '/products'
          }
        }
      }

      return item;
        
    })

    return subCats;

  }, [props.subCats])

  // Errors
  if (props.statusCode > 299) {
    return <Error statusCode={props.statusCode} />
  }

  return (
    <>
    
      <Head>
        <title>{seo.createTitle(seoContent?.title)}</title>
        <meta name="description" content={seoContent?.description} />
      </Head>

      <StyledPage className="categoryPage">

        <PageWrapper>

          {/* Header */}
          <SbEditable content={props.pageData}>
            <BlockHeaderWhiteBox 
              block={{
                header_image: content.cover_photo,
                header_alt_text: content.cover_photo_alt_text,
                title: content.name,
                intro: content.intro
              }} 
              renderContent={content.search_url ? () => (
                <Link href={{ pathname: '/products', query: searchUrlQuery }}>
                  <Button
                    height="36px"
                    corners="4px"
                    fontSize="12px"
                    color="blue"
                  >
                    VIEW ALL PRODUCTS
                  </Button>
                </Link>
              ): null}
            />
          </SbEditable>


          {/* Content 1 */}
          {props.pageData?.content?.content1 &&
            <ContentArea content={props.pageData?.content?.content1} />
          }
          

          {/* Sub Categories */}
          {subCats && subCats.length > 0 &&
            <div>

              <ContentWrapper>
                <h3 className="spacingBottomHalf padding">View Our Range</h3>
              </ContentWrapper>

              <ContentWrapper fullWidthAt={[1,2]}>

                <Grid className="spacingBottom">
                  {subCats.map(item => (
                    <Card
                      key={item.uuid}
                      mainImageUrl={item.content.cover_photo}
                      useImagePlaceholder={true}
                      title={item.content.name}
                      text={item.content.card_summary}
                      link={item.content.link}
                      linkText="VIEW PRODUCTS"
                    />
                  ))}
                </Grid>

              </ContentWrapper>

            </div>
          }


          {/* Content 2 */}
          {props.pageData?.content?.content2 &&
            <ContentArea content={props.pageData?.content?.content2} />
          }


          {/* Top Selling Products */}
          {props.pageData?.content.top_products && props.pageData.content.top_products.length > 0 &&
            <div>

                <ContentWrapper>
                  <h3 className="spacingBottomHalf">Top Selling Products In {content.name}</h3>
                </ContentWrapper>

                <ContentWrapper fullWidthAt={[1,2]}>

                  <Grid className="spacingBottom">
                    {props.pageData.content.top_products.map(item => (
                      <ProductCard
                        key={item.uuid}
                        product={item}
                      />
                    ))}
                  </Grid>

                </ContentWrapper>

            </div>
              
          }


          {/* Content 3 */}
          {props.pageData?.content?.content3 &&
            <ContentArea content={props.pageData?.content?.content3} />
          }

          <div style={{height: '3rem'}}></div>

        </PageWrapper>

      </StyledPage>

      {storyblokService.bridge()}
        
    </>
  );

}



// Static Paths
export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking'
  }
}


// Server/Static Props
export async function getStaticProps({ params, query, preview = false, previewData = {} }) {

  // let slug = params.slug && params.slug.length > 0 ? params.slug[0] : '' - Put this back if we stop using folders

  // Create full slug
  let slug = params.slug
  slug = slug.join('/')


  let statusCode = 200

  // Get Data
  let results

  let getCat = `cdn/stories/category/${slug}`
  let getChildLinks = `category/${slug}`

  if(!getChildLinks.endsWith('/')){
    getChildLinks += '/'
  }

  try {
    results = await Promise.all([
      getTemplateData(),
      storyblokService.get(getCat, {resolve_relations: 'top_products', excluding_fields: 'card_summary'}),
      storyblokService.get(`cdn/links`, {starts_with: getChildLinks})
    ]);
  } catch (error) {
    return {
      revalidate: 10, // Put this back if moving back to static props
      props: { 
        statusCode: error.response ? error.response.status : 500
      }
    }
  }

  const masterTemplateData = results[0] ?? null;
  const pageData = results[1]?.data?.story ?? null;
  const childLinks = results[2]?.data.links ?? null;

  // Hack because last items keeps going missing
  masterTemplateData.content.top_menu_categories.push({});
    

  // Filter the child links
  let childIds: string[] = [];

  // We want to get links in the same folder, or the index of subfolders
  // Were interested in links which have a slug which has two more slashes than the current page slug and doesn't end with a slash
  // Or slugs which have 3 more slashes than the current page slug and one of them is at the end.

  const slashesInPageSlug = (slug.match(/\//g) || []).length;
  const minSlashesCount = slashesInPageSlug + 2;
  const maxSlashesCount = slashesInPageSlug + 3;

  for (const [ id, item ] of Object.entries(childLinks)) {

    const link:any  = item;

    if(!link.is_folder){

      let count = (link.slug.match(/\//g) || []).length;

      if(count === minSlashesCount && !link.slug.endsWith('/')){
        childIds.push(link.uuid);
      }

      if(count === maxSlashesCount && link.slug.endsWith('/')){
        childIds.push(link.uuid);
      }

    }
      
  }

  // Get the children
  let subCatsResult

  if(childIds && childIds.length > 0){
      
    try {
      subCatsResult = await storyblokService.get(`cdn/stories/`, {
        by_uuids: childIds.join(','),
        sort_by: 'content.priority:asc'
      });
    } catch (error) {
      return {
        revalidate: 10, // Put this back if moving back to static props
        props: { 
          statusCode: error.response ? error.response.status : 500
        }
      };
    }

  }

  let subCats = subCatsResult?.data?.stories || [];

  // Sort the subcategories by priority
  subCats = subCats.sort((a, b) => a.content.priority - b.content.priority);

  return {
    revalidate: 10, // Put this back if moving back to static props
    props: { 
      getCat,
      getChildLinks,
      pageData: pageData || null,
      childLinks: childLinks || null,
      subCats: subCats || null,
      statusCode,
      slug: slug || null,
      masterTemplateData: masterTemplateData || null
    }
  };

}

Page.getLayout = (page: ReactElement) => {
  return (
    <MainLayout masterTemplateData={page.props.masterTemplateData}>
      {page}
    </MainLayout>
  );
}

export default Page;


// Styles
const StyledPage = styled.div``;



